import { Reducer } from "react";
import { AbilityPhase, AbilityUsage } from "./abilities";

// RoleDistributor 게임 상태 관리 리듀서
// 라벨, 유저, 배정 상태 등 관리

export interface SpecialRole {
  name: string;
  count: number;
  explain: string;
  abilities?: string[]; // 역할에 배정된 능력 ID 배열
}

export interface DefaultRole {
  name: string;
  explain: string;
  abilities?: string[]; // 기본 역할도 능력 보유 가능
}

export interface RolePlayer {
  id: number;
  name: string;
  label?: string; // 배정된 역할
  isHost: boolean; // 방장 여부
  roleExplain: string;
  abilities?: string[]; // 플레이어가 보유한 능력 ID 배열
}

export interface AbilityPhaseResult {
  timestamp: number;
  results: Array<{
    message: string;
    type: "public" | "private";
    targetPlayerId?: number;
    abilityId?: string;
    success: boolean;
  }>;
}

export interface RoleDistributorState {
  labels: SpecialRole[];
  defaultLabel: DefaultRole;
  players: RolePlayer[];
  hostReveal: boolean; // 호스트 공개 모드
  started: boolean;
  hostId: number; // 방장 ID
  settingsCompleted: boolean; // 설정 완료 상태
  abilityPhase: AbilityPhase; // 특수능력 단계 상태
  abilityUsages: AbilityUsage[]; // 능력 사용 기록
  pastAbilityResults: AbilityUsage[][]; // 과거 능력 단계 결과들 (legacy)
  abilityHistory: AbilityPhaseResult[]; // 새로운 히스토리 시스템
}

export type RoleDistributorAction =
  | { type: "SET_PLAYERS"; players: RolePlayer[] }
  | { type: "ADD_PLAYER"; player: RolePlayer }
  | { type: "REMOVE_PLAYER"; playerId: number }
  | { type: "UPDATE_PLAYER"; playerId: number; updates: Partial<RolePlayer> }
  | { type: "SET_HOST"; hostId: number }
  | { type: "ADD_LABEL"; label: SpecialRole }
  | { type: "REMOVE_LABEL"; name: string }
  | { type: "UPDATE_LABEL"; name: string; count: number; explain: string; abilities?: string[] }
  | { type: "SET_LABELS"; labels: SpecialRole[] }
  | { type: "SET_DEFAULT_LABEL"; defaultLabel: DefaultRole }
  | { type: "SET_HOST_REVEAL"; value: boolean }
  | { type: "SET_STARTED"; started: boolean }
  | { type: "SET_SETTINGS_COMPLETED"; completed: boolean }
  | {
      type: "COMPLETE_SETTINGS";
      defaultLabel: DefaultRole;
      labels: SpecialRole[];
      hostReveal: boolean;
    }
  | { type: "RESET_SETTINGS" }
  | { type: "START_DISTRIBUTION"; players: RolePlayer[] } // 역할 룰렛 시작
  | { type: "START_ABILITY_PHASE"; startedBy: number; duration?: number; endTime?: number }
  | { type: "END_ABILITY_PHASE" }
  | { type: "CANCEL_ABILITY"; playerId: number; abilityId: string }
  | { type: "USE_ABILITY"; usage: AbilityUsage }
  | { type: "ADD_PENDING_RESULT"; usage: AbilityUsage }
  | { type: "PROCESS_PENDING_RESULTS"; processedResults: AbilityUsage[] }
  | { type: "SAVE_ABILITY_RESULTS"; results: AbilityUsage[] }
  | { type: "ADD_ABILITY_HISTORY"; phaseResult: AbilityPhaseResult }
  | { type: "CLEAR_ABILITY_HISTORY" }
  | { type: "SET_ROLE_ABILITIES"; roleName: string; abilities: string[]; isDefault?: boolean }
  | { type: "CLEAR_ABILITY_USAGES" }
  | { type: "RESET" };

export const initialRoleDistributorState: RoleDistributorState = {
  labels: [], // RoleLabel 배열 (빈 배열로 시작)
  defaultLabel: { name: "", explain: "" }, // 기본 라벨 초기값
  players: [],
  hostReveal: false,
  started: false,
  hostId: -1, // 초기값 추가
  settingsCompleted: false, // 설정 완료 상태 초기값
  abilityPhase: { isActive: false, startedBy: -1, pendingResults: [] }, // 특수능력 단계 초기값
  abilityUsages: [], // 능력 사용 기록 초기값
  pastAbilityResults: [], // 과거 능력 결과 초기값
  abilityHistory: [], // 새로운 히스토리 시스템 초기값
};

export const roleDistributorReducer: Reducer<
  RoleDistributorState,
  RoleDistributorAction
> = (state, action) => {
  switch (action.type) {
    case "SET_PLAYERS":
      return { ...state, players: action.players };
    case "ADD_PLAYER":
      return { ...state, players: [...state.players, action.player] };
    case "REMOVE_PLAYER":
      return {
        ...state,
        players: state.players.filter((p) => p.id !== action.playerId),
      };
    case "UPDATE_PLAYER":
      return {
        ...state,
        players: state.players.map((p) =>
          p.id === action.playerId ? { ...p, ...action.updates } : p
        ),
      };
    case "SET_HOST":
      return {
        ...state,
        hostId: action.hostId,
        players: state.players.map((p) => ({
          ...p,
          isHost: p.id === action.hostId,
        })),
      };
    case "ADD_LABEL":
      // RoleLabel 객체를 배열에 추가
      return {
        ...state,
        labels: [...state.labels, action.label],
      };
    case "REMOVE_LABEL":
      return {
        ...state,
        labels: state.labels.filter((label) => label.name !== action.name),
      };
    case "UPDATE_LABEL":
      return {
        ...state,
        labels: state.labels.map((label) =>
          label.name === action.name
            ? { ...label, count: action.count, explain: action.explain, abilities: action.abilities }
            : label
        ),
      };
    case "SET_LABELS":
      return {
        ...state,
        labels: action.labels,
      };
    case "SET_DEFAULT_LABEL":
      return {
        ...state,
        defaultLabel: action.defaultLabel,
      };
    case "SET_HOST_REVEAL":
      return { ...state, hostReveal: action.value };
    case "SET_STARTED":
      return { ...state, started: action.started };
    case "SET_SETTINGS_COMPLETED":
      return { ...state, settingsCompleted: action.completed };
    case "COMPLETE_SETTINGS":
      // 설정 완료 - 서버에서 받은 설정으로 확정
      return {
        ...state,
        settingsCompleted: true,
        defaultLabel: action.defaultLabel,
        labels: action.labels,
        hostReveal: action.hostReveal,
      };
    case "RESET_SETTINGS":
      // 설정 초기화 - 다시 수정 가능한 상태로
      return {
        ...state,
        settingsCompleted: false,
      };
    case "START_DISTRIBUTION":
      // 역할 룰렛 시작
      return {
        ...state,
        started: true,
        players: action.players, // 서버에서 받은 역할 배정된 플레이어들로 업데이트
      };
    case "START_ABILITY_PHASE":
      return {
        ...state,
        abilityPhase: {
          isActive: true,
          startedBy: action.startedBy,
          startTime: Date.now(),
          duration: action.duration,
          endTime: action.endTime,
          pendingResults: [] // 새로운 단계 시작 시 펜딩 결과 초기화
        }
      };
    case "END_ABILITY_PHASE":
      return {
        ...state,
        abilityPhase: {
          isActive: false,
          startedBy: -1,
          startTime: undefined,
          duration: undefined,
          endTime: undefined,
          pendingResults: [] // 단계 종료 시 펜딩 결과 초기화
        }
      };
    case "CANCEL_ABILITY":
      // 현재 페이즈에서 해당 플레이어의 해당 액션 취소
      const phaseStart = state.abilityPhase.startTime || 0;
      return {
        ...state,
        abilityUsages: state.abilityUsages.filter(
          (usage) =>
            !(
              usage.playerId === action.playerId &&
              usage.abilityId === action.abilityId &&
              usage.timestamp >= phaseStart
            )
        )
      };
    case "USE_ABILITY":
      return {
        ...state,
        abilityUsages: [...state.abilityUsages, action.usage]
      };
    case "ADD_PENDING_RESULT":
      return {
        ...state,
        abilityPhase: {
          ...state.abilityPhase,
          pendingResults: [...state.abilityPhase.pendingResults, action.usage]
        }
      };
    case "PROCESS_PENDING_RESULTS":
      return {
        ...state,
        abilityUsages: [...state.abilityUsages, ...action.processedResults],
        abilityPhase: {
          ...state.abilityPhase,
          pendingResults: [] // 처리 후 펜딩 결과 초기화
        }
      };
    case "SAVE_ABILITY_RESULTS":
      return {
        ...state,
        pastAbilityResults: [...state.pastAbilityResults, action.results]
      };
    case "SET_ROLE_ABILITIES":
      if (action.isDefault) {
        return {
          ...state,
          defaultLabel: {
            ...state.defaultLabel,
            abilities: action.abilities
          }
        };
      } else {
        return {
          ...state,
          labels: state.labels.map((label) =>
            label.name === action.roleName
              ? { ...label, abilities: action.abilities }
              : label
          )
        };
      }
    case "CLEAR_ABILITY_USAGES":
      return {
        ...state,
        abilityUsages: []
      };
    case "ADD_ABILITY_HISTORY":
      // 최신 결과를 앞에 추가 (최신순)
      const newHistory = [action.phaseResult, ...state.abilityHistory];
      // 최대 10개만 유지
      return {
        ...state,
        abilityHistory: newHistory.slice(0, 10)
      };
    case "CLEAR_ABILITY_HISTORY":
      return {
        ...state,
        abilityHistory: []
      };
    case "RESET":
      return initialRoleDistributorState;
    default:
      return state;
  }
};
